import { z } from 'zod'
import { utf8Length } from '../textBytes'

/**
 * Yeniden kullanılabilir zod alan kuralları (020). Mesajlar i18n anahtarı (`validation.*`) taşır;
 * gösterimde `resolveFieldError` ile çevrilir (messages.ts). **Tip-değiştiren** transform kullanılmaz
 * (`.trim()` gibi string→string transformlar var; ama boş→null / string→number gibi tip değiştiren
 * dönüşümler yok) — böylece `z.input === z.output` kalır ve useZodForm'un tek `z.infer` generic'i sağlam
 * çalışır; boş→null / sayıya çevirme gönderim (onValid) eşlemesinde yapılır.
 */

/**
 * Hedef kolonun **bayt** sınırı (`maxBytes`) — karakter sınırından ayrı bir kuraldır.
 *
 * <p>Oracle kolonları BYTE semantiğindedir ve Türkçe harfler UTF-8'de 2 bayt tutar (bkz.
 * `lib/textBytes.ts`), yani karakter sınırına uyan bir metin yine de kolona sığmayabilir. Sunucu bunu
 * `@MaxBytes` ile reddeder (`TEXT_TOO_LONG`); bu kural, kullanıcının gönderime kadar beklemeden alanın
 * altında uyarı görmesi içindir. **Sunucudaki kısıtın yerini tutmaz** — istemci doğrulaması atlanabilir.
 */
function withMaxBytes<T extends z.ZodString>(s: T, maxBytes?: number) {
  if (maxBytes == null) return s
  return s.refine((v) => utf8Length(v) <= maxBytes, 'validation.maxBytes')
}

/**
 * Zorunlu metin (trim'lenip boş olamaz); opsiyonel üst uzunluk (karakter) ve opsiyonel bayt sınırı.
 * Tip: string.
 */
export function requiredText(max?: number, maxBytes?: number) {
  let s = z.string().trim().min(1, 'validation.required')
  if (max != null) s = s.max(max, 'validation.maxLength')
  return withMaxBytes(s, maxBytes)
}

/**
 * Opsiyonel metin (boş kabul); opsiyonel üst uzunluk (karakter) ve opsiyonel bayt sınırı.
 * Tip: string (boş = sağlanmadı).
 */
export function optionalText(max?: number, maxBytes?: number) {
  let s = z.string().trim()
  if (max != null) s = s.max(max, 'validation.maxLength')
  return withMaxBytes(s, maxBytes)
}

/** Zorunlu + biçim (regex). Tip: string. (Ör. PNR: zorunlu + 6 alfanumerik.) */
export function regexField(re: RegExp, messageKey: string) {
  return z.string().trim().min(1, 'validation.required').regex(re, messageKey)
}

/**
 * Opsiyonel tam sayı metni: boşsa geçerli; doluysa yalnız `^\d+$`. Tip: string (ayraç/ondalık yok →
 * locale karışması engellenir). Sayıya çevirme gönderim eşlemesinde yapılır.
 */
export function intString() {
  return z.string().refine((v) => {
    const s = v.trim()
    return s === '' || /^\d+$/.test(s)
  }, 'validation.integer')
}

/**
 * Zorunlu seçim (id). Seçilmemiş = null; null iken 'validation.required'. Tip: number | null.
 * `superRefine` kullanılır çünkü Zod v4'te `.nullable().refine(...)` çıktı tipini `number`'a
 * daraltır (v3'ten farklı); `superRefine` tipi bozmaz → infer `number | null` kalır ve boş
 * (null) varsayılan atanabilir. Gönderimde (onValid) non-null garanti edilir.
 */
export function requiredId() {
  return z
    .number()
    .nullable()
    .superRefine((v, ctx) => {
      if (v === null) ctx.addIssue({ code: 'custom', message: 'validation.required' })
    })
}

/** Zorunlu sayı (0 dahil geçerli; null iken 'validation.required'). Tip: number | null. (Ör. sıra.) */
export const requiredNumber = requiredId

/*
 * **E-posta listesi kuralı (`emailListText`) KALDIRILDI** (2026-08-04): tek kullanıcısı bildirim şablonu
 * formundaki Kime/CC/BCC alanlarıydı; bu alanlar artık parametre paneli hedefi olduğundan
 * `${MEETING.OWNER_EMAIL}` gibi yer tutucu taşıyabiliyor — biçim kuralı meşru girdiyi reddediyordu.
 * Alanlar `optionalText(1000)` ile yalnız uzunluk sınırına tabidir; ayraç (`;`) normalizasyonu sunucuda
 * durur (`common/validation/EmailList.normalize`).
 */

/*
 * **Yer tutucu kodu kuralı (`placeholderCode`) KALDIRILDI** (2026-08-04): parametre kodu artık hiçbir
 * formda girilmiyor — Şablon Parametreleri ekranında kod salt-okunurdur ve yeni parametre eklenmiyor.
 * Biçim doğrulaması sunucuda duruyor (`TemplateParameterAdminService.CODE_PATTERN`, spec FR-026);
 * çağrılmayan bir istemci kuralı ise ekranda olmayan bir girdinin izlenimini bırakırdı.
 */
